import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthContext";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../Services/Store";
import { fetchUserInfo, fetchUserAccess } from "../../Services/Slices/authSlice";
import WebService from "../../Services/WebService";
import { PORTAL_AUTH_CLIENT } from "../../Services/authClient";
import Swal from "sweetalert2";
import { toast } from "react-toastify"; 
import "./Otp.css";

const OTP_LENGTH = 6;

type VerifyOtpResponse = {
  status_code: number;
  message?: string;
  token?: string;
  is_new_user?: boolean;
};

type ResendOtpResponse = {
  status_code: number;
  message?: string;
};

// ---- ERROR HANDLER FUNCTION ----
function getOtpErrorMessage(err: any, type: "verify" | "resend" = "verify") {
  let errorMsg = "Something went wrong. Please try again.";
  if (err?.response) {
    const code = err.response.status || err.response.data?.status_code;
    const msg = err.response.data?.message || err.message;

    if (code === 404)
      errorMsg =
        type === "verify"
          ? "OTP expired or not found. Please request a new OTP."
          : "Could not find your user or OTP session. Try logging in again.";
    else if (code === 400)
      errorMsg =
        msg || (type === "verify" ? "Invalid OTP." : "Unable to resend OTP.");
    else if (code === 401)
      errorMsg = "Session expired or unauthorized. Please login again.";
    else if (code === 500)
      errorMsg = "Server error. Please try again later.";
    else errorMsg = msg || errorMsg;
  } else if (err?.status_code === 404) {
    errorMsg =
      type === "verify"
        ? "OTP expired or not found. Please request a new OTP."
        : "Could not find your user or OTP session. Try logging in again.";
  } else if (typeof err === "string") {
    errorMsg = err;
  } else if (err?.message) {
    errorMsg = err.message;
  }
  return errorMsg;
}
// ---- END ERROR HANDLER ----

const VerifyOtp = () => {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const resendInterval = 30;

  const navigate = useNavigate();
  const location = useLocation();
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Track mount status
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    toast.dismiss(); 
    return () => {
      isMounted.current = false;
    };
  }, []);

  const { setAuthenticated } = useAuth();
  const dispatch = useDispatch<AppDispatch>();

  const mobile = (location.state as any)?.mobile || "";
  const otpType = (location.state as any)?.type || "LOGIN";

  useEffect(() => {
    if (!mobile || !otpType) {
      navigate("/login");
    }
  }, [mobile, otpType, navigate]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pasted)) return;
    const digits = pasted.slice(0, OTP_LENGTH).split("");
    const filled = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < digits.length; i++) filled[i] = digits[i];
    setOtp(filled);
    const focusIdx = Math.min(digits.length, OTP_LENGTH - 1);
    setTimeout(() => inputsRef.current[focusIdx]?.focus(), 0);
  };

  const parseApi = <T,>(raw: any): T => {
    return (raw?.data ?? raw) as T;
  };

  const getStatusCode = (raw: any): number => {
    const p = raw?.data ?? raw;
    if (typeof p?.status_code === "number") return p.status_code;
    if (typeof p?.data?.status_code === "number") return p.data.status_code;
    return 0;
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const joinedOtp = otp.join("");
    if (joinedOtp.length < OTP_LENGTH) {
      if (isMounted.current && location.pathname === "/verifyOtp") {
        Swal.fire({
          icon: "warning",
          title: "Incomplete OTP",
          text: `Please enter all ${OTP_LENGTH} digits.`,
          confirmButtonColor: "#FFD400",
          heightAuto: false,
        });
      }
      setLoading(false);
      return;
    }

    if (!/^\d{6}$/.test(joinedOtp)) {
      if (isMounted.current && location.pathname === "/verifyOtp") {
        Swal.fire({
          icon: "error",
          title: "Invalid OTP",
          text: "Please enter valid OTP.",
          confirmButtonColor: "#FFD400",
          heightAuto: false,
        });
      }
      setLoading(false);
      return;
    }

    try {
      const raw = await WebService.postAPI({
        action: "/v1/door-management/verify-otp",
        body: { mobile_number: mobile, otp: joinedOtp, type: otpType, client: PORTAL_AUTH_CLIENT },
        isShowError: false,
      });

      const payload = parseApi<VerifyOtpResponse | any>(raw);
      const statusCode = getStatusCode(raw);
      const token = payload?.token ?? payload?.data?.token;
      const isNewUser = payload?.is_new_user ?? payload?.data?.is_new_user;

      if (statusCode === 200) {
        toast.dismiss();
        if (isMounted.current && location.pathname === "/verifyOtp") {
          Swal.fire({
            icon: "success",
            title: "OTP Verified",
            showConfirmButton: false,
            timer: 900,
            heightAuto: false,
          });
        }

        if (otpType === "NEW_REGISTRATION" || isNewUser) {
          navigate("/register", { state: { mobile }, replace: true });
        } else if (otpType === "FORGET_PASSWORD" && token) {
          navigate("/resetPassword", { state: { mobile, token }, replace: true });
        } else {
          const effectiveToken = token || localStorage.getItem("token");
          if (effectiveToken) {
            localStorage.setItem("token", effectiveToken);
            setAuthenticated(true);
          }

          const userRes: any = await dispatch(fetchUserInfo());
          await dispatch(fetchUserAccess());

          const userType = (
            (fetchUserInfo.fulfilled.match(userRes) && userRes.payload?.user_type) ||
            ""
          ).toString().toUpperCase();

          if (userType === "CUSTOMER") {
            navigate("/shop", { replace: true });
          } else {
            // ADMIN, EMPLOYEE, or unknown — enter app; App routes by role
            navigate("/", { replace: true });
          }
        }
      } else {
        if (isMounted.current && location.pathname === "/verifyOtp") {
          Swal.fire({
            icon: "error",
            title: "Invalid OTP",
            text: "Please enter valid OTP.",
            confirmButtonColor: "#FFD400",
            heightAuto: false,
          });
        }
      }
    } catch (err: any) {
      if (isMounted.current && location.pathname === "/verifyOtp") {
        Swal.fire({
          icon: "error",
          title: "OTP Error",
          text: getOtpErrorMessage(err, "verify"),
          confirmButtonColor: "#FFD400",
          heightAuto: false,
        });
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleResend = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (resendTimer > 0 || resendLoading) return;
    setResendLoading(true);

    try {
      const raw = await WebService.postAPI({
        action: "/v1/door-management/resend-otp",
        body: { mobile_number: mobile, type: otpType },
        isShowError: false,
      });

      const payload = parseApi<ResendOtpResponse | any>(raw);
      const statusCode = getStatusCode(raw);

      if (statusCode === 200) {
        if (isMounted.current && location.pathname === "/verifyOtp") {
          Swal.fire({
            icon: "success",
            title: "OTP Resent",
            text: "A new OTP has been sent to your mobile.",
            confirmButtonColor: "#FFD400",
            timer: 1500,
            heightAuto: false,
          });
        }
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeout(() => inputsRef.current[0]?.focus(), 0);
        setResendTimer(resendInterval);
      } else {
        if (isMounted.current && location.pathname === "/verifyOtp") {
          Swal.fire({
            icon: "error",
            title: "Failed to Resend OTP",
            text: payload?.message || "Failed to resend OTP. Try again.",
            confirmButtonColor: "#FFD400",
            heightAuto: false,
          });
        }
      }
    } catch (err: any) {
      if (isMounted.current && location.pathname === "/verifyOtp") {
        Swal.fire({
          icon: "error",
          title: "Resend OTP Error",
          text: getOtpErrorMessage(err, "resend"),
          confirmButtonColor: "#FFD400",
          heightAuto: false,
        });
      }
    }
    setResendLoading(false);
  };

  return (
    <div
      className="otp-bg"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL || ""}/login.png)`,
      }}
    >
      <form className="otp-float-form" onSubmit={handleOtpSubmit} noValidate>
        <img src="/Assets/dm.png" alt="Logo" className="otp-logo" />
        <h2 className="otp-heading">
          Verify OTP
        </h2>
        <p className="otp-subheading">
          Please enter the OTP sent to your {mobile ? <b>{mobile}</b> : "mobile number"}.
        </p>

        <div className="otp-box-group">
          {otp.map((digit, idx) => (
            <input
              key={idx}
              type="text"
              inputMode="numeric"
              maxLength={1}
              className="otp-box"
              value={digit}
              ref={(el) => {
                inputsRef.current[idx] = el;
              }}
              onChange={(e) => handleChange(idx, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              onPaste={idx === 0 ? handlePaste : undefined}
              autoFocus={idx === 0}
            />
          ))}
        </div>

        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? "Verifying..." : "Verify OTP"}
        </button>

        <div className="otp-resend-row">
          <span className="otp-resend-text">
            Didn’t receive the OTP?{" "}
            <button
              type="button"
              className={`login-register-link otp-resend-btn${
                resendTimer > 0 || resendLoading ? " otp-resend-btn--disabled" : ""
              }`}
              onClick={handleResend}
              disabled={resendTimer > 0 || resendLoading}
            >
              {resendTimer > 0
                ? `Resend in ${resendTimer}s`
                : resendLoading
                ? "Resending..."
                : "Resend"}
            </button>
          </span>
        </div>
      </form>
    </div>
  );
};

export default VerifyOtp;
